import React,{useState,useEffect} from 'react';
import {motion} from 'framer-motion';
import {brandKitsApi} from '../../lib/brandKitsApi';
import {useBrandKit} from '../../contexts/BrandKitContext';
import {useNotification} from '../../contexts/NotificationContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import SafePre from '../SafePre';

const {FiDatabase,FiRefreshCw,FiPlus,FiTrash2,FiEdit2,FiCopy,FiDownload,FiCheck,FiX,FiLoader}=FiIcons;

export default function BrandKitDebugger() {
  const {brandKits,loadBrandKits,currentBrandKit}=useBrandKit();
  const {showSuccess,showError}=useNotification();
  const [debugLogs,setDebugLogs]=useState([]);
  const [testKit,setTestKit]=useState({
    name: 'Debug Brand Kit',
    account_id: import.meta.env.VITE_BRAND_ACCOUNT_ID || 'client-main',
    colors: {
      primary: '#03245E',
      accent: '#E2C164',
      background: '#F3F7FA',
      secondary: '#D7B740',
      highlight: '#F7EFE2'
    },
    fonts: {
      titles: 'Anton',
      body: 'Helvetica World',
      subtitle: 'Montserrat',
      heading: 'League Spartan',
      highlight: 'Gistesy'
    },
    watermark_settings: {
      opacity: 0.3,
      position: 'bottom-right',
      size: 'medium'
    },
    default_margins: {
      top: 20,
      right: 20,
      bottom: 20,
      left: 20
    },
    logos: [],
    watermarks: [],
    selected_logo_id: null,
    selected_watermark_id: null
  });
  const [isTesting,setIsTesting]=useState(false);
  const [testResults,setTestResults]=useState(null);
  const [serverEnvStatus,setServerEnvStatus]=useState(null);
  const [listResult,setListResult]=useState(null);
  const [listError,setListError]=useState(null);
  const [createResult,setCreateResult]=useState(null);
  const [createError,setCreateError]=useState(null);
  const [getResult,setGetResult]=useState(null);
  const [getError,setGetError]=useState(null);
  const [updateResult,setUpdateResult]=useState(null);
  const [updateError,setUpdateError]=useState(null);
  const [deleteResult,setDeleteResult]=useState(null);
  const [deleteError,setDeleteError]=useState(null);

  const logMessage=(message,type='info')=> {
    const timestamp=new Date().toISOString();
    const logEntry={id: Date.now(),timestamp,message,type};
    setDebugLogs(prev=> [logEntry,...prev.slice(0,49)]);
    console.log(`[${timestamp}] ${type.toUpperCase()}: ${message}`);
  };

  const logSuccess=(message,details=null)=> {
    logMessage(`${message}${details ? `: ${JSON.stringify(details)}` : ''}`,'success');
  };

  const checkServerEnv=async ()=> {
    try {
      const response=await fetch('/api/brand-kits');
      if (response.ok) {
        setServerEnvStatus('ok');
      } else {
        const data=await response.json();
        setServerEnvStatus(data.error || 'Unknown error');
      }
    } catch (error) {
      setServerEnvStatus(error.message || 'Connection failed');
    }
  };

  const runDatabaseTests=async ()=> {
    setIsTesting(true);
    setTestResults(null);
    setListResult(null);
    setListError(null);
    setCreateResult(null);
    setCreateError(null);
    setGetResult(null);
    setGetError(null);
    setUpdateResult(null);
    setUpdateError(null);
    setDeleteResult(null);
    setDeleteError(null);
    
    logMessage('Starting database connectivity tests...','info');
    
    try {
      // Test 1: Health check
      logMessage('Test 1: Checking API health...','info');
      const healthResponse=await fetch('/api/health');
      const healthData=await healthResponse.json();
      logSuccess('Health check result',healthData);

      // Test 2: List brand kits
      logMessage('Test 2: Fetching brand kits from database...','info');
      const kits=await brandKitsApi.list();
      logSuccess(`Retrieved ${kits.length} brand kits`);
      let createdId=null;

      // 1) LIST
      logMessage('Test 3: Listing brand kits via direct API...','info');
      console.info('[DBG] calling /api/brand-kits ...');
      const listResponse=await fetch('/api/brand-kits');
      let listPayload;
      try { 
        listPayload = await listResponse.json(); 
      } catch { 
        listPayload = { status:'failed', error:'Non-JSON response' }; 
      }
      setListResult(listPayload);
      setListError(!listResponse.ok ? (listPayload?.error ?? 'Request failed') : null);
      logSuccess('Listed brand kits via direct API',listPayload);

      // 2) CREATE
      logMessage('Test 4: Creating test brand kit...','info');
      console.info('[DBG] calling /api/brand-kits ...');
      const createRes=await fetch('/api/brand-kits',{
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          brand_kit_configuration: JSON.stringify(testKit)
        })
      });
      const createText=await createRes.text();
      let createJson;
      try {
        createJson=JSON.parse(createText);
      } catch {
        createJson = { status:'failed', error:`Non-JSON response: ${createText}` };
      }
      setCreateResult(createJson);
      setCreateError(!createRes.ok ? (createJson?.error ?? 'Request failed') : null);
      
      createdId=createJson?.id ?? createJson?.data?.id;
      if (!createdId) {
        throw new Error(`No id returned from create — body: ${JSON.stringify(createJson)}`);
      }
      logSuccess(`Created brand kit with ID: ${createdId}`);

      // 3) GET
      logMessage('Test 5: Retrieving created brand kit...','info');
      if (!createdId) throw new Error('No active id to operate on');
      console.info('[DBG] calling /api/brand-kits ...');
      const getRes=await fetch(`/api/brand-kits/${createdId}`);
      const getText=await getRes.text();
      let getJson;
      try {
        getJson=JSON.parse(getText);
      } catch {
        getJson = { status:'failed', error:`Non-JSON response: ${getText}` };
      }
      setGetResult(getJson);
      setGetError(!getRes.ok ? (getJson?.error ?? 'Request failed') : null);
      logSuccess('Retrieved brand kit',getJson);

      // 4) UPDATE (never read id from response)
      logMessage('Test 6: Updating brand kit...','info');
      if (!createdId) throw new Error('No active id to operate on');
      console.info('[DBG] calling /api/brand-kits ...');
      const updRes=await fetch(`/api/brand-kits/${createdId}`,{
        method: 'PUT',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          brand_kit_configuration: JSON.stringify({
            ...testKit,
            name: `${testKit.name} (updated)`,
            updated_at: new Date().toISOString()
          })
        })
      });
      const updateText=await updRes.text();
      let updateJson;
      try {
        updateJson=JSON.parse(updateText);
      } catch {
        updateJson = { status:'failed', error:`Non-JSON response: ${updateText}` };
      }
      setUpdateResult(updateJson);
      setUpdateError(!updRes.ok ? (updateJson?.error ?? 'Request failed') : null);
      logSuccess(`Updated brand kit: ${updateJson?.message || 'OK'}`);

      // 5) DELETE (still use createdId)
      logMessage('Test 7: Deleting test brand kit...','info');
      if (!createdId) throw new Error('No active id to operate on');
      console.info('[DBG] calling /api/brand-kits ...');
      const delRes=await fetch(`/api/brand-kits/${createdId}`,{
        method: 'DELETE'
      });
      const delText=await delRes.text();
      let delJson;
      try {
        delJson=JSON.parse(delText);
      } catch {
        delJson = { status:'failed', error:`Non-JSON response: ${delText}` };
      }
      setDeleteResult(delJson);
      setDeleteError(!delRes.ok ? (delJson?.error ?? 'Request failed') : null);
      logSuccess('Successfully deleted test brand kit');

      // 6) CLEAR id to avoid post-delete reads
      const finalCreatedId=createdId;
      createdId=null;
      
      setTestResults({
        status: 'success',
        message: 'All database tests passed successfully!',
        details: {
          health: healthData,
          kitCount: kits.length,
          createdKitId: finalCreatedId,
        }
      });
      
      showSuccess('All database tests passed!');
    } catch (error) {
      logMessage(`Database test failed: ${error.message}`,'error');
      setTestResults({
        status: 'error',
        message: 'Database test failed',
        error: error.message,
        stack: error.stack
      });
      showError(`Database test failed: ${error.message}`);
    } finally {
      setIsTesting(false);
    }
  };

  const refreshBrandKits=async ()=> {
    logMessage('Refreshing brand kits from database...','info');
    try {
      await loadBrandKits();
      logMessage('Brand kits refreshed successfully','success');
      showSuccess('Brand kits refreshed');
    } catch (error) {
      logMessage(`Failed to refresh brand kits: ${error.message}`,'error');
      showError(`Failed to refresh brand kits: ${error.message}`);
    }
  };

  const exportLogs=()=> {
    const logText=debugLogs.map(log=> 
      `[${log.timestamp}] ${log.type.toUpperCase()}: ${log.message}`
    ).join('\n');
    
    const blob=new Blob([logText],{type: 'text/plain'});
    const url=URL.createObjectURL(blob);
    const a=document.createElement('a');
    a.href=url;
    a.download=`brandkit-debug-logs-${new Date().toISOString().slice(0,19)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    logMessage('Debug logs exported','info');
  };

  const clearLogs=()=> {
    setDebugLogs([]);
    logMessage('Debug logs cleared','info');
  };

  useEffect(()=> {
    logMessage('Brand Kit Debugger initialized','info');
    logMessage(`Current environment: ${import.meta.env.MODE}`,'info');
    
    // Log environment variables (without exposing secrets)
    const envVars={
      BRAND_ACCOUNT_ID: 'SET (server-side)',
      VITE_BRAND_ACCOUNT_ID: import.meta.env.VITE_BRAND_ACCOUNT_ID ? 'SET' : 'NOT SET'
    };
    logMessage(`Environment variables: ${JSON.stringify(envVars)}`,'info');
    
    // Check server environment
    checkServerEnv();
  },[]);

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Brand Kit Database Debugger</h1>
        <p className="text-gray-600">Comprehensive debugging tools for NCB brand kit operations</p>
      </div>

      {/* Server Environment Check */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Server Environment Check</h2>
        <div className={`p-4 rounded-lg border-2 ${
          serverEnvStatus==='ok' ? 'bg-green-50 border-green-200' : 
          serverEnvStatus ? 'bg-red-50 border-red-200' : 
          'bg-gray-100 border-gray-200'
        }`}>
          <div className="flex items-center">
            {serverEnvStatus==='ok' ? (
              <>
                <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600 mr-3" />
                <div>
                  <p className="font-medium text-gray-900">Server environment OK</p>
                  <p className="text-sm text-gray-600">API endpoints are properly configured</p>
                </div>
              </>
            ) : serverEnvStatus ? (
              <>
                <SafeIcon icon={FiX} className="w-5 h-5 text-red-600 mr-3" />
                <div>
                  <p className="font-medium text-gray-900">Server environment error</p>
                  <p className="text-sm text-red-600">{serverEnvStatus}</p>
                </div>
              </>
            ) : (
              <>
                <SafeIcon icon={FiLoader} className="w-5 h-5 text-gray-600 mr-3 animate-spin" />
                <div>
                  <p className="font-medium text-gray-900">Checking server environment...</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Test Controls */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Database Testing</h2>
          <div className="flex space-x-2">
            <motion.button
              whileHover={{scale: 1.02}}
              whileTap={{scale: 0.98}}
              onClick={refreshBrandKits}
              className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2" />
              Refresh Brand Kits
            </motion.button>
            <motion.button
              whileHover={{scale: 1.02}}
              whileTap={{scale: 0.98}}
              onClick={runDatabaseTests}
              disabled={isTesting}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                isTesting ? 
                'bg-gray-300 text-gray-500 cursor-not-allowed' : 
                'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isTesting ? (
                <>
                  <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <SafeIcon icon={FiDatabase} className="w-4 h-4 mr-2" />
                  Run Database Tests
                </>
              )}
            </motion.button>
          </div>
        </div>
        
        {testResults && (
          <div className={`p-4 rounded-lg border-2 ${
            testResults.status==='success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center">
              <SafeIcon 
                icon={testResults.status==='success' ? FiCheck : FiX} 
                className={`w-5 h-5 mr-3 ${
                  testResults.status==='success' ? 'text-green-600' : 'text-red-600'
                }`} 
              />
              <div>
                <p className="font-medium text-gray-900">
                  {testResults.message}
                </p>
                {testResults.details && (
                  <pre className="text-xs text-gray-600 mt-2 overflow-auto max-h-32">
                    {JSON.stringify(testResults.details,null,2)}
                  </pre>
                )}
                {testResults.error && (
                  <pre className="text-xs text-red-600 mt-2 overflow-auto max-h-32">
                    {testResults.error}
                  </pre>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* API Test Results */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* List Results */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">List Brand Kits</h3>
          {listError ? (
            <div className="p-3 bg-red-50 text-red-700 rounded mb-3">
              <strong>Error:</strong> {listError}
            </div>
          ) : null}
          <div className="border rounded p-3 bg-gray-50">
            <SafePre value={listResult} className="text-xs overflow-auto max-h-40" />
          </div>
        </div>

        {/* Create Results */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Create Brand Kit</h3>
          {createError ? (
            <div className="p-3 bg-red-50 text-red-700 rounded mb-3">
              <strong>Error:</strong> {createError}
            </div>
          ) : null}
          <div className="border rounded p-3 bg-gray-50">
            <SafePre value={createResult} className="text-xs overflow-auto max-h-40" />
          </div>
        </div>

        {/* Get Results */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Get Brand Kit</h3>
          {getError ? (
            <div className="p-3 bg-red-50 text-red-700 rounded mb-3">
              <strong>Error:</strong> {getError}
            </div>
          ) : null}
          <div className="border rounded p-3 bg-gray-50">
            <SafePre value={getResult} className="text-xs overflow-auto max-h-40" />
          </div>
        </div>

        {/* Update Results */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Update Brand Kit</h3>
          {updateError ? (
            <div className="p-3 bg-red-50 text-red-700 rounded mb-3">
              <strong>Error:</strong> {updateError}
            </div>
          ) : null}
          <div className="border rounded p-3 bg-gray-50">
            <SafePre value={updateResult} className="text-xs overflow-auto max-h-40" />
          </div>
        </div>

        {/* Delete Results */}
        <div className="bg-white rounded-lg shadow-md p-6 lg:col-span-2">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Delete Brand Kit</h3>
          {deleteError ? (
            <div className="p-3 bg-red-50 text-red-700 rounded mb-3">
              <strong>Error:</strong> {deleteError}
            </div>
          ) : null}
          <div className="border rounded p-3 bg-gray-50">
            <SafePre value={deleteResult} className="text-xs overflow-auto max-h-40" />
          </div>
        </div>
      </div>

      {/* Current State */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Current Brand Kits</h2>
          <div className="space-y-3">
            {brandKits && brandKits.length > 0 ? (
              brandKits.map((kit)=> (
                <div 
                  key={kit.id} 
                  className={`p-3 rounded-lg border-2 ${
                    currentBrandKit?.id===kit.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{kit.name}</h3>
                      <p className="text-sm text-gray-600">ID: {kit.id}</p>
                    </div>
                    <div className="flex space-x-1">
                      <button 
                        onClick={()=> navigator.clipboard.writeText(JSON.stringify(kit,null,2))}
                        className="p-1 text-gray-500 hover:text-blue-600"
                        title="Copy kit data"
                      >
                        <SafeIcon icon={FiCopy} className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    Created: {kit.created_at || 'N/A'} | Updated: {kit.updated_at || 'N/A'}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <SafeIcon icon={FiDatabase} className="w-12 h-12 mx-auto mb-4" />
                <p>No brand kits found</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Active Brand Kit</h2>
          {currentBrandKit ? (
            <div className="space-y-3">
              <div className="p-3 rounded-lg border border-gray-200 bg-gray-50">
                <h3 className="font-medium text-gray-900">{currentBrandKit.name}</h3>
                <p className="text-sm text-gray-600">ID: {currentBrandKit.id}</p>
                <div className="mt-2 text-xs text-gray-500">
                  Created: {currentBrandKit.created_at || 'N/A'} | Updated: {currentBrandKit.updated_at || 'N/A'}
                </div>
              </div>
              <button
                onClick={()=> {
                  const data=JSON.stringify(currentBrandKit,null,2);
                  navigator.clipboard.writeText(data);
                  showSuccess('Brand kit data copied to clipboard');
                }}
                className="w-full py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Copy Active Kit Data
              </button>
              <button
                onClick={()=> {
                  const blob=new Blob([JSON.stringify(currentBrandKit,null,2)],{type: 'application/json'});
                  const url=URL.createObjectURL(blob);
                  const a=document.createElement('a');
                  a.href=url;
                  a.download=`brand-kit-${currentBrandKit.id}.json`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
                className="w-full py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Export Active Kit
              </button>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <SafeIcon icon={FiDatabase} className="w-12 h-12 mx-auto mb-4" />
              <p>No active brand kit selected</p>
            </div>
          )}
        </div>
      </div>

      {/* Debug Logs */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Debug Logs</h2>
          <div className="flex space-x-2">
            <motion.button
              whileHover={{scale: 1.02}}
              whileTap={{scale: 0.98}}
              onClick={exportLogs}
              className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <SafeIcon icon={FiDownload} className="w-4 h-4 mr-2" />
              Export Logs
            </motion.button>
            <motion.button
              whileHover={{scale: 1.02}}
              whileTap={{scale: 0.98}}
              onClick={clearLogs}
              className="flex items-center px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
            >
              <SafeIcon icon={FiTrash2} className="w-4 h-4 mr-2" />
              Clear Logs
            </motion.button>
          </div>
        </div>
        <div className="bg-gray-900 rounded-lg p-4 h-96 overflow-y-auto font-mono text-sm">
          {debugLogs.length > 0 ? (
            debugLogs.map((log)=> (
              <div 
                key={log.id} 
                className={`mb-2 ${
                  log.type==='error' ? 'text-red-400' : 
                  log.type==='success' ? 'text-green-400' : 
                  log.type==='warning' ? 'text-yellow-400' : 
                  'text-gray-300'
                }`}
              >
                <span className="text-gray-500">[{log.timestamp}]</span>{' '}
                <span className="font-semibold">[{log.type.toUpperCase()}]:</span>{' '}
                {log.message}
              </div>
            ))
          ) : (
            <div className="text-gray-500 h-full flex items-center justify-center">
              No debug logs yet. Perform some operations to see logs here.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}